import React, { useEffect, useState } from 'react';
import { PiInfoLight } from 'react-icons/pi';
import { FunnelSelector } from 'src/modules/shared/components/molecules/funnel-selector';
import { NamedOptionSingleSelect } from 'src/modules/shared/components/molecules/named-option-single-select';
import { NamedOptionToggleList } from 'src/modules/shared/components/molecules/named-option-toggle-list';
import {
  type Funnel,
  type FunnelCustomFieldDefinitionWithPosition,
  type FunnelStatus,
  type ReportDashboardWidgetInstance,
  type ReportUserOption,
} from 'src/api/generated';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import MultiSelect from 'src/modules/shared/components/atoms/inputs/multi-select';
import { Tab } from 'src/modules/shared/components/atoms/tabs/tab';
import { Tabs } from 'src/modules/shared/components/atoms/tabs/tabs';
import { Text } from 'src/modules/shared/components/atoms/text';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { classes } from 'src/modules/shared/utils/styles.util';
import { ReportDashboardWidgetCard } from './report-dashboard-widget-card';
import {
  type DashboardWidgetKind,
  getWidgetUserIds,
  getWidgetFunnelId,
  getWidgetTitleLabel,
  hasRequiredWidgetFilters,
  supportsWidgetUserFilter,
  updateWidgetBreakdownField,
  updateWidgetColor,
  updateWidgetDateRange,
  updateWidgetFunnel,
  updateWidgetLabel,
  updateWidgetLimit,
  updateWidgetStatusId,
  updateWidgetStatusIds,
  updateWidgetTitle,
  updateWidgetUserIds,
  updateWidgetVisibleStatusIds,
  usesFunnel,
} from './reports-dashboard.utils';
import { REPORT_WIDGET_INPUT_CLASSES } from './reports-dashboard.constants';
import { ReportDateRangeSelector } from './report-date-range-selector';
import {
  DEFAULT_REPORT_WIDGET_ACCENT_COLOR,
  REPORT_WIDGET_ACCENT_COLOR_OPTIONS,
} from './report-widget-accent-color';
import { getReportDateRangeValidationMessage } from '../utils/report-date-range';
import { WidgetWarningState } from './widget-warning-state';
import {
  isFunnelStatusFunnelWidget,
  isFunnelStatusTimeseriesWidget,
  isPropertyInterestRankingWidget,
  isUserAssignmentRankingWidget,
} from '../utils/report-dashboard';

type MobileEditorSection = 'settings' | 'preview';

const FIELD_LABEL_TEXT_CLASS = 'uppercase tracking-wide text-neutral-500';

const EditorFieldLabel: React.FC<{
  label: string;
  hint?: string;
}> = ({ label, hint }) => (
  <div className="flex items-center gap-1.5">
    <Text
      as="span"
      size="tiny"
      weight="medium"
      className={FIELD_LABEL_TEXT_CLASS}
    >
      {label}
    </Text>
    {hint ? (
      <Tooltip
        content={hint}
        placement="top"
        size="sm"
        variant="primary"
        as="span"
      >
        <span className="inline-flex cursor-help items-center text-primary-400 transition hover:text-primary-600">
          <PiInfoLight className="h-4 w-4" />
        </span>
      </Tooltip>
    ) : null}
  </div>
);

const EditorFieldHint: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => (
  <Text size="tiny" className="text-neutral-500">
    {children}
  </Text>
);

/**
 * Widget configuration panel with live preview.
 */
export const WidgetEditorPanel: React.FC<{
  draft: ReportDashboardWidgetInstance;
  widgetTitleByKind: Map<DashboardWidgetKind, string>;
  onChange: React.Dispatch<
    React.SetStateAction<ReportDashboardWidgetInstance | null>
  >;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  funnels: Funnel[];
  funnelsLoading: boolean;
  statuses: FunnelStatus[];
  statusesLoading: boolean;
  userOptions: ReportUserOption[];
  usersLoading: boolean;
  breakdownDefinitions: FunnelCustomFieldDefinitionWithPosition[];
  previewDraft: ReportDashboardWidgetInstance | null;
  inModal?: boolean;
}> = ({
  draft,
  widgetTitleByKind,
  onChange,
  onCancel,
  onSave,
  saving,
  funnels,
  funnelsLoading,
  statuses,
  statusesLoading,
  userOptions,
  usersLoading,
  breakdownDefinitions,
  previewDraft,
  inModal = false,
}) => {
  const { t } = useI18n();
  const [mobileSection, setMobileSection] =
    useState<MobileEditorSection>('settings');
  const draftDateRangeError = getReportDateRangeValidationMessage(
    draft.filters.dateRange,
    t
  );
  const previewDateRangeError = previewDraft
    ? getReportDateRangeValidationMessage(previewDraft.filters.dateRange, t)
    : null;
  const canPreview = Boolean(
    previewDraft &&
      hasRequiredWidgetFilters(previewDraft) &&
      !previewDateRangeError
  );
  const requiresFunnel = usesFunnel(draft.kind);
  const requiresStatuses =
    isFunnelStatusFunnelWidget(draft) ||
    isFunnelStatusTimeseriesWidget(draft) ||
    isUserAssignmentRankingWidget(draft);
  const selectedFunnelId = getWidgetFunnelId(draft);
  const hasNoFunnels =
    requiresFunnel && !funnelsLoading && funnels.length === 0;
  const hasNoStatuses =
    requiresStatuses &&
    Boolean(selectedFunnelId) &&
    !statusesLoading &&
    statuses.length === 0;

  useEffect(() => {
    setMobileSection('settings');
  }, [draft.id]);

  const handleWidgetChange = (
    updater: (
      current: ReportDashboardWidgetInstance
    ) => ReportDashboardWidgetInstance
  ) => {
    onChange((current) => (current ? updater(current) : current));
  };

  return (
    <div className="grid min-h-0 max-h-full w-full flex-1 rounded-2xl border border-neutral-200 bg-white shadow-sm grid-cols-1 xl:h-full xl:max-h-full xl:grid-cols-[minmax(380px,460px)_minmax(0,1fr)] xl:grid-rows-[minmax(0,1fr)] xl:overflow-hidden 2xl:grid-cols-[minmax(420px,500px)_minmax(0,1fr)]">
      <div className="border-b border-neutral-200 bg-neutral-50/80 px-4 py-3 xl:hidden">
        <Tabs
          value={mobileSection}
          onChange={(value) => setMobileSection(value as MobileEditorSection)}
          size="md"
          variant="secondary"
          appearance="outline"
          className="bg-white"
        >
          <Tab
            value="settings"
            label={t('reports.dashboard.editor.mobile.settings')}
          />
          <Tab
            value="preview"
            label={t('reports.dashboard.editor.mobile.preview')}
          />
        </Tabs>
      </div>

      <div
        className={classes(
          'flex min-h-0 flex-col overflow-hidden p-4 sm:p-5 xl:h-full xl:max-h-full xl:border-r xl:border-neutral-200',
          mobileSection === 'preview' && 'hidden xl:block'
        )}
      >
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto xl:max-h-full">
          {!inModal && (
            <div className="space-y-1">
              <Text
                as="h3"
                size="h6"
                weight="semibold"
                className="text-primary-900"
              >
                {t('reports.dashboard.editor.title')}
              </Text>
              <Text size="small" className="text-neutral-600">
                {t('reports.dashboard.editor.description')}
              </Text>
            </div>
          )}

          {hasNoFunnels && (
            <WidgetWarningState
              title={t('reports.dashboard.warnings.noFunnelsTitle')}
              issues={[t('reports.dashboard.warnings.noFunnelsDescription')]}
            />
          )}

          {hasNoStatuses && (
            <WidgetWarningState
              title={t('reports.dashboard.warnings.noStatusesTitle')}
              issues={[t('reports.dashboard.warnings.noStatusesDescription')]}
            />
          )}

          <label className="block space-y-1">
            <EditorFieldLabel
              label={t('reports.dashboard.fields.title.label')}
              hint={t('reports.dashboard.fields.title.hint')}
            />
            <input
              className={REPORT_WIDGET_INPUT_CLASSES}
              value={draft.title ?? ''}
              onChange={(event) =>
                handleWidgetChange((current) =>
                  updateWidgetTitle(current, event.target.value)
                )
              }
              placeholder={getWidgetTitleLabel(
                draft.kind,
                widgetTitleByKind,
                t('reports.dashboard.widgetFallbackTitle')
              )}
            />
            <EditorFieldHint>
              {t('reports.dashboard.fields.title.helper')}
            </EditorFieldHint>
          </label>

          <label className="block space-y-1">
            <EditorFieldLabel
              label={t('reports.dashboard.fields.label.label')}
              hint={t('reports.dashboard.fields.label.hint')}
            />
            <input
              className={REPORT_WIDGET_INPUT_CLASSES}
              value={draft.label ?? ''}
              onChange={(event) =>
                handleWidgetChange((current) =>
                  updateWidgetLabel(current, event.target.value)
                )
              }
              placeholder={t('reports.dashboard.fields.label.placeholder')}
            />
            <EditorFieldHint>
              {t('reports.dashboard.fields.label.helper')}
            </EditorFieldHint>
          </label>

          <div className="space-y-2">
            <EditorFieldLabel
              label={t('reports.dashboard.fields.color.label')}
              hint={t('reports.dashboard.fields.color.hint')}
            />
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {REPORT_WIDGET_ACCENT_COLOR_OPTIONS.map((option) => {
                const isSelected =
                  (draft.color ?? DEFAULT_REPORT_WIDGET_ACCENT_COLOR) ===
                  option.value;

                return (
                  <button
                    key={option.value}
                    type="button"
                    className={classes(
                      'flex items-center gap-2 rounded-xl border px-3 py-2 text-left shadow-sm transition hover:border-neutral-300',
                      isSelected
                        ? `ring-2 ${option.selectedClassName}`
                        : 'border-neutral-200 bg-white'
                    )}
                    onClick={() =>
                      handleWidgetChange((current) =>
                        updateWidgetColor(current, option.value)
                      )
                    }
                  >
                    <span
                      className={classes(
                        'h-8 w-10 rounded-lg border shadow-sm',
                        option.surfaceClassName
                      )}
                    >
                      <span
                        className={classes(
                          'mx-auto mt-2 block h-3.5 w-3.5 rounded-full border border-white shadow-sm',
                          option.swatchClassName
                        )}
                      />
                    </span>
                    <Text
                      as="span"
                      size="small"
                      weight={isSelected ? 'medium' : 'regular'}
                      className="text-primary-900"
                    >
                      {t(option.labelKey)}
                    </Text>
                  </button>
                );
              })}
            </div>
            <EditorFieldHint>
              {t('reports.dashboard.fields.color.helper')}
            </EditorFieldHint>
          </div>

          <div className="space-y-1">
            <EditorFieldLabel
              label={t('reports.dashboard.fields.dateRange.label')}
              hint={t('reports.dashboard.fields.dateRange.hint')}
            />
            <ReportDateRangeSelector
              value={draft.filters.dateRange}
              errorMessage={draftDateRangeError}
              onChange={(dateRange) =>
                handleWidgetChange((current) =>
                  updateWidgetDateRange(current, dateRange)
                )
              }
            />
            <EditorFieldHint>
              {t('reports.dashboard.fields.dateRange.helper')}
            </EditorFieldHint>
          </div>

          {supportsWidgetUserFilter(draft.kind) && (
            <label className="block space-y-1">
              <EditorFieldLabel
                label={t('reports.dashboard.fields.users.label')}
                hint={t('reports.dashboard.fields.users.hint')}
              />
              <MultiSelect
                size="sm"
                variant="secondary"
                disabled={!usersLoading && userOptions.length === 0}
                loading={usersLoading}
                value={getWidgetUserIds(draft).map(String)}
                onChange={(selectedUserIds) =>
                  handleWidgetChange((current) =>
                    updateWidgetUserIds(
                      current,
                      selectedUserIds.map((userId) => Number(userId))
                    )
                  )
                }
                options={userOptions.map((user) => ({
                  content: user.label,
                  value: String(user.id),
                }))}
                containerClassName="w-full"
              />
              <EditorFieldHint>
                {usersLoading
                  ? t('reports.dashboard.fields.users.loadingMessage')
                  : userOptions.length > 0
                    ? t('reports.dashboard.fields.users.helper')
                    : t('reports.dashboard.fields.users.emptyMessage')}
              </EditorFieldHint>
            </label>
          )}

          {usesFunnel(draft.kind) && (
            <div className="space-y-1">
              <EditorFieldLabel
                label={t('reports.dashboard.fields.funnel.label')}
                hint={t('reports.dashboard.fields.funnel.hint')}
              />
              <FunnelSelector
                funnels={funnels}
                selectedFunnelId={getWidgetFunnelId(draft)}
                onFunnelSelect={(funnelId) =>
                  handleWidgetChange((current) =>
                    updateWidgetFunnel(current, funnelId)
                  )
                }
                loading={funnelsLoading}
                size="sm"
              />
              <EditorFieldHint>
                {t('reports.dashboard.fields.funnel.helper')}
              </EditorFieldHint>
            </div>
          )}

          {isFunnelStatusFunnelWidget(draft) && (
            <div className="space-y-1">
              <NamedOptionToggleList
                title={t('reports.dashboard.fields.visibleStatuses.label')}
                options={statuses}
                loading={statusesLoading}
                selectedIds={draft.filters.statusIds}
                loadingMessage={t(
                  'reports.dashboard.fields.statuses.loadingMessage'
                )}
                emptyMessage={t(
                  'reports.dashboard.fields.statuses.emptyMessage'
                )}
                onToggle={(statusId) =>
                  handleWidgetChange((current) => {
                    if (!isFunnelStatusFunnelWidget(current)) {
                      return current;
                    }

                    const selected = new Set(current.filters.statusIds);
                    if (selected.has(statusId)) {
                      selected.delete(statusId);
                    } else {
                      selected.add(statusId);
                    }

                    return updateWidgetStatusIds(
                      current,
                      statuses
                        .filter((status) => selected.has(status.id))
                        .map((status) => status.id)
                    );
                  })
                }
              />
              <EditorFieldHint>
                {t('reports.dashboard.fields.visibleStatuses.helper')}
              </EditorFieldHint>
            </div>
          )}

          {isFunnelStatusTimeseriesWidget(draft) && (
            <div className="space-y-1">
              <NamedOptionSingleSelect
                title={t('reports.dashboard.fields.status.label')}
                options={statuses}
                loading={statusesLoading}
                value={draft.filters.statusId ?? null}
                placeholder={t('reports.dashboard.fields.status.placeholder')}
                onChange={(statusId) =>
                  handleWidgetChange((current) =>
                    updateWidgetStatusId(current, statusId)
                  )
                }
              />
              <EditorFieldHint>
                {t('reports.dashboard.fields.status.helper')}
              </EditorFieldHint>
            </div>
          )}

          {isUserAssignmentRankingWidget(draft) && (
            <div className="space-y-1">
              <NamedOptionToggleList
                title={t('reports.dashboard.fields.visibleColumns.label')}
                options={statuses}
                loading={statusesLoading}
                selectedIds={draft.filters.visibleStatusIds}
                loadingMessage={t(
                  'reports.dashboard.fields.statuses.loadingMessage'
                )}
                emptyMessage={t(
                  'reports.dashboard.fields.statuses.emptyMessage'
                )}
                onToggle={(statusId) =>
                  handleWidgetChange((current) => {
                    if (!isUserAssignmentRankingWidget(current)) {
                      return current;
                    }

                    const selected = new Set(current.filters.visibleStatusIds);
                    if (selected.has(statusId)) {
                      selected.delete(statusId);
                    } else {
                      selected.add(statusId);
                    }

                    return updateWidgetVisibleStatusIds(
                      current,
                      statuses
                        .filter((status) => selected.has(status.id))
                        .map((status) => status.id)
                    );
                  })
                }
              />
              <EditorFieldHint>
                {t('reports.dashboard.fields.visibleColumns.helper')}
              </EditorFieldHint>
            </div>
          )}

          {(isFunnelStatusFunnelWidget(draft) ||
            isFunnelStatusTimeseriesWidget(draft)) && (
            <label className="block space-y-1">
              <EditorFieldLabel
                label={t('reports.dashboard.fields.breakdown.label')}
                hint={t('reports.dashboard.fields.breakdown.hint')}
              />
              <select
                className={REPORT_WIDGET_INPUT_CLASSES}
                value={draft.filters.breakdownFieldDefinitionId ?? ''}
                onChange={(event) =>
                  handleWidgetChange((current) =>
                    updateWidgetBreakdownField(
                      current,
                      event.target.value ? Number(event.target.value) : null
                    )
                  )
                }
              >
                <option value="">
                  {t('reports.dashboard.fields.breakdown.none')}
                </option>
                {breakdownDefinitions.map((definition) => (
                  <option key={definition.id} value={definition.id}>
                    {definition.fieldLabel ?? definition.fieldKey}
                  </option>
                ))}
              </select>
              <EditorFieldHint>
                {t('reports.dashboard.fields.breakdown.helper')}
              </EditorFieldHint>
            </label>
          )}

          {isPropertyInterestRankingWidget(draft) && (
            <label className="block space-y-1">
              <EditorFieldLabel
                label={t('reports.dashboard.fields.limit.label')}
                hint={t('reports.dashboard.fields.limit.hint')}
              />
              <input
                type="number"
                min={1}
                max={50}
                className={REPORT_WIDGET_INPUT_CLASSES}
                value={draft.filters.limit ?? 10}
                onChange={(event) =>
                  handleWidgetChange((current) => {
                    const nextLimit = Number(event.target.value);
                    return updateWidgetLimit(
                      current,
                      nextLimit > 0 ? nextLimit : 10
                    );
                  })
                }
              />
              <EditorFieldHint>
                {t('reports.dashboard.fields.limit.helper')}
              </EditorFieldHint>
            </label>
          )}
        </div>

        {!inModal ? (
          <div className="sticky bottom-0 -mx-4 flex flex-wrap gap-2 border-t border-neutral-200 bg-white px-4 py-3 sm:-mx-5 sm:px-5 xl:static xl:mx-0 xl:border-t-0 xl:bg-transparent xl:px-0 xl:pb-0">
            <Button
              size="sm"
              variant="secondary"
              appearance="outline"
              onClick={onCancel}
            >
              {t('reports.dashboard.actions.cancel')}
            </Button>
            <Button
              size="sm"
              appearance="soft"
              variant="secondary"
              onClick={onSave}
              loading={saving}
              disabled={Boolean(draftDateRangeError)}
            >
              {t('reports.dashboard.actions.save')}
            </Button>
          </div>
        ) : null}
      </div>

      <div
        className={classes(
          'flex min-h-0 flex-col overflow-hidden bg-gradient-to-br from-neutral-50 via-white to-neutral-50 p-4 sm:p-5 xl:h-full xl:max-h-full',
          mobileSection === 'settings' && 'hidden xl:block'
        )}
      >
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto xl:max-h-full xl:overflow-y-visible">
          <div className="space-y-1">
            <Text
              as="h3"
              size="h6"
              weight="semibold"
              className="text-primary-900"
            >
              {t('reports.dashboard.preview.title')}
            </Text>
            <Text size="small" className="text-neutral-600">
              {t('reports.dashboard.preview.description')}
            </Text>
            <EditorFieldHint>
              {t('reports.dashboard.preview.helper')}
            </EditorFieldHint>
          </div>
          {previewDraft ? (
            <ReportDashboardWidgetCard
              widget={previewDraft}
              widgetTitleByKind={widgetTitleByKind}
              isPreview
              previewEnabled={canPreview}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};
